'use client';

import { FC } from 'react';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Users } from 'lucide-react';
import InputField from './InputField';
import PrimaryButton from './PrimaryButton';
import AuthFooter from './AuthFooter';
import shellStyles from './AuthShell.module.scss';
import styles from './EmailScreen.module.scss';
interface EmailScreenProps {
  onSubmit: (email: string) => void | Promise<void>;
  onGoogleLogin?: () => void;
  onAppleLogin?: () => void;
  onSwitchToSignUp: () => void;
  googleLoginEnabled?: boolean;
  appleLoginEnabled?: boolean;
  termLink?: string;
  privacyPolicyLink?: string;
  isSubmitting?: boolean;
}
const EmailScreen: FC<EmailScreenProps> = ({
  onSubmit,
  onGoogleLogin,
  onAppleLogin,
  onSwitchToSignUp,
  googleLoginEnabled = true,
  appleLoginEnabled = false,
  termLink,
  privacyPolicyLink,
  isSubmitting = false
}) => {
  const t = useTranslations();
  const schema = yup.object().shape({
    email: yup.string().email(t('workspace-details.form.email-error')).max(100, t('workspace-details.form.email-error')).required(t('auth.error'))
  });
  const {
    register,
    handleSubmit,
    trigger,
    formState: {
      errors
    }
  } = useForm<{
    email: string;
  }>({
    resolver: yupResolver(schema)
  });
  const submit = handleSubmit(async data => {
    await onSubmit(data.email);
  });
  return <>
            <div className={shellStyles.iconBubble}>
                <span className={shellStyles.iconBubbleInner}>
                    <Users size={32} strokeWidth={1.75} data-sentry-element="Users" data-sentry-source-file="EmailScreen.tsx" />
                </span>
            </div>

            <form className={styles.form} onSubmit={submit} noValidate>
                <InputField type="email" autoComplete="email" placeholder={t('auth.email')} error={errors.email?.message} {...register('email', {
        onBlur: () => trigger('email')
      })} data-sentry-element="InputField" data-sentry-source-file="EmailScreen.tsx" />

                <PrimaryButton type="submit" disabled={isSubmitting} data-sentry-element="PrimaryButton" data-sentry-source-file="EmailScreen.tsx">
                    {t('auth.submit')}
                </PrimaryButton>

                <p className={styles.signup}>
                    {t('auth.noAccount')}{' '}
                    <button type="button" className={styles.signupLink} onClick={onSwitchToSignUp}>
                        {t('auth.signup')}
                    </button>
                </p>

                {(googleLoginEnabled || appleLoginEnabled) && <div className={styles.divider}>
                        <span className={styles.dividerLine} />
                        <span className={styles.dividerLabel}>
                            {t('auth.login')}
                        </span>
                        <span className={styles.dividerLine} />
                    </div>}

                <div className={styles.socials}>
                    {googleLoginEnabled && onGoogleLogin && <PrimaryButton variant="secondary" onClick={onGoogleLogin} iconLeft={<Image src="/assets/icons/google.svg" width={20} height={20} alt="" />}>
                            {t('auth.google')}
                        </PrimaryButton>}
                    {appleLoginEnabled && onAppleLogin && <PrimaryButton variant="secondary" onClick={onAppleLogin} iconLeft={<Image src="/assets/icons/apple-auth.svg" width={20} height={20} alt="" />}>
                            {t('auth.apple')}
                        </PrimaryButton>}
                </div>
            </form>

            <AuthFooter termLink={termLink} privacyPolicyLink={privacyPolicyLink} data-sentry-element="AuthFooter" data-sentry-source-file="EmailScreen.tsx" />
        </>;
};
export default EmailScreen;